import React from 'react';
import { FileEntry } from '../../types/capstone';

interface Props {
  files: FileEntry[];
  containerState: string | null;
  selectedPath?: string | null;
  onSelect?: (path: string) => void;
}

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FileTree: React.FC<Props> = ({ files, containerState, selectedPath, onSelect }) => {
  if (files.length === 0) {
    // Asleep (hibernated/stopped) ≠ empty: the workspace is snapshotted to
    // blob (v3.22) and comes back on the next build message — say so instead
    // of implying the files are gone (#117 guidance-first).
    const asleep = containerState === 'stopped' || containerState === 'hibernating';
    return (
      <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
        <div className="text-4xl mb-2 opacity-30">{asleep ? '🌙' : '{ }'}</div>
        {asleep ? (
          <>
            <p>Project is asleep — your files are saved.</p>
            <p className="mt-2 text-xs opacity-70">
              They'll be restored automatically as the project wakes up.
            </p>
          </>
        ) : (
          <>
            <p>Files will appear here as the AI writes them.</p>
            {containerState && containerState !== 'ready' && (
              <p className="mt-2 text-xs italic opacity-70">Container: {containerState}…</p>
            )}
          </>
        )}
      </div>
    );
  }

  // Sort: directories logically grouped (by prefix), files alphabetical
  const sorted = [...files].sort((a, b) => a.path.localeCompare(b.path));

  return (
    <div className="h-full overflow-y-auto p-3">
      <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2 px-2">
        Files · {files.length}
      </div>
      <ul className="space-y-0.5">
        {sorted.map((f) => {
          const isSelected = selectedPath === f.path;
          return (
            <li key={f.path}>
              <button
                type="button"
                onClick={() => onSelect?.(f.path)}
                className={`w-full flex items-center justify-between gap-2 px-2 py-1 rounded text-sm font-mono text-left transition-colors ${
                  isSelected
                    ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-900 dark:text-purple-200'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-800/60 text-gray-800 dark:text-gray-200'
                }`}
              >
                <span className="truncate" title={f.path}>{f.path}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">
                  {formatSize(f.size)}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default FileTree;
